import React, { useState, useEffect, useRef } from 'react';
import { Image, Plus, Trash2, Edit2, Upload, Eye, EyeOff, GripVertical, Loader2, X, Link, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminHomepageBanners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    image_url: '',
    link_url: '',
    button_text: '',
    order: 0,
    is_active: true
  });
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/extended/homepage-banners`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBanners(data.banners || []);
      }
    } catch (error) {
      console.error('Failed to load banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title) {
      toast({ title: "Error", description: "Please fill the title", variant: "destructive" });
      return;
    }

    if (!formData.image_url) {
      toast({ title: "Error", description: "Please upload an image", variant: "destructive" });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const url = editingBanner 
        ? `${API_URL}/api/admin/extended/homepage-banners/${editingBanner.id}`
        : `${API_URL}/api/admin/extended/homepage-banners`;
      
      const response = await fetch(url, {
        method: editingBanner ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast({ title: "Success", description: editingBanner ? "Banner updated" : "Banner created" });
        setShowForm(false);
        setEditingBanner(null);
        resetForm();
        loadBanners();
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to save banner", variant: "destructive" });
    }
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm('Delete this banner?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/admin/extended/homepage-banners/${bannerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast({ title: "Deleted" });
      loadBanners();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: "Invalid File", description: "Please upload PNG, JPG, GIF, or WebP", variant: "destructive" });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "File Too Large", description: "Maximum file size is 5MB", variant: "destructive" });
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${API_URL}/api/admin/upload/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, image_url: data.url }));
        toast({ title: "Image Uploaded", description: "Banner image uploaded successfully" });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      toast({ title: "Upload Failed", description: "Could not upload image. Please try again.", variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      image_url: '',
      link_url: '',
      button_text: '',
      order: banners.length,
      is_active: true
    });
  };

  const startEdit = (banner) => {
    setEditingBanner(banner);
    setFormData(banner);
    setShowForm(true);
  };

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Homepage Banners</h1>
          <p className="text-gray-600">Manage hero sliders and promotional banners</p>
        </div>
        <Button onClick={() => { resetForm(); setEditingBanner(null); setShowForm(true); }} className="bg-mint-500 hover:bg-mint-600">
          <Plus className="w-4 h-4 mr-2" /> Add Banner
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editingBanner ? 'Edit Banner' : 'New Banner'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X /></Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
                  placeholder="Find Your Perfect Match"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Subtitle</Label>
                <Input
                  value={formData.subtitle}
                  onChange={(e) => setFormData(p => ({ ...p, subtitle: e.target.value }))}
                  placeholder="Join thousands of happy couples"
                />
              </div>

              <div className="space-y-2">
                <Label>Banner Image *</Label>
                <div className="space-y-2">
                  {formData.image_url && (
                    <div className="relative">
                      <img src={formData.image_url} alt="Preview" className="w-full h-40 object-cover rounded-lg" />
                      <button
                        type="button"
                        onClick={() => setFormData(p => ({ ...p, image_url: '' }))}
                        className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex-1"
                    >
                      {uploading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                      ) : (
                        <><Upload className="w-4 h-4 mr-2" /> {formData.image_url ? 'Change Image' : 'Upload Image'}</>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">PNG, JPG, GIF, WebP (max 5MB). Recommended: 1920x600px</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Button Text</Label>
                  <Input
                    value={formData.button_text}
                    onChange={(e) => setFormData(p => ({ ...p, button_text: e.target.value }))}
                    placeholder="Get Started"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Button Link</Label>
                  <Input
                    value={formData.link_url}
                    onChange={(e) => setFormData(p => ({ ...p, link_url: e.target.value }))}
                    placeholder="/register"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Display Order</Label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData(p => ({ ...p, order: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(c) => setFormData(p => ({ ...p, is_active: c }))}
                  />
                  <Label>Active</Label>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowForm(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleSubmit} className="flex-1 bg-mint-500 hover:bg-mint-600">
                  {editingBanner ? 'Update' : 'Create'} Banner
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Banners List */}
      <div className="space-y-4">
        {banners.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              <Image className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No banners yet</p>
              <p className="text-sm">Add your first homepage banner</p>
            </CardContent>
          </Card>
        ) : (
          banners.map((banner, index) => (
            <Card key={banner.id} className={!banner.is_active ? 'opacity-60' : ''}>
              <CardContent className="p-4">
                <div className="flex gap-4">
                  <div className="flex items-center text-gray-400">
                    <GripVertical className="w-5 h-5" />
                    <span className="ml-1 font-mono text-sm">{banner.order}</span>
                  </div>
                  
                  {banner.image_url && (
                    <img src={banner.image_url} alt={banner.title} className="w-40 h-24 object-cover rounded-lg flex-shrink-0" />
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold">{banner.title}</h3>
                        {banner.subtitle && <p className="text-sm text-gray-500">{banner.subtitle}</p>}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => startEdit(banner)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(banner.id)} className="text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
                      {banner.button_text && (
                        <span className="flex items-center gap-1">
                          <Link className="w-3 h-3" />
                          {banner.button_text}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        {banner.is_active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {banner.is_active ? 'Active' : 'Hidden'}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminHomepageBanners;
